import { useState } from "react";
import { Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PrimaryCTA, TextCTA } from "@/components/CTA";
import { LINKS, ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

const VIMEO_ID = "1211881897";
const VIMEO_SRC = `https://player.vimeo.com/video/${VIMEO_ID}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&autoplay=1`;
// Existing Zahos case-study URL (Insights CMS, slug "zahos-xygas") — never invent a new one.
const ZAHOS_CASE_HREF = `${ROUTES.caseStudies}/zahos-xygas`;

/**
 * Section 6 — Client story + video.
 * Featured text case study (Pantelis) + a compact, click-to-play video
 * card (Marilena). The video never auto-loads or autoplays — it only
 * mounts the Vimeo iframe once the visitor explicitly presses play, and
 * stays small/secondary to the case-study copy per spec.
 */
const VimeoPlayer = ({ testId }) => (
  <iframe
    src={VIMEO_SRC}
    title="Marilena Katsimi — video testimonial for InterAct Globally"
    data-testid={testId}
    className="absolute inset-0 w-full h-full"
    frameBorder="0"
    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
    referrerPolicy="strict-origin-when-cross-origin"
    allowFullScreen
  />
);

export const CaseStudies = () => {
  const { t } = useLang();
  const [desktopPlaying, setDesktopPlaying] = useState(false);
  const [mobilePlaying, setMobilePlaying] = useState(false);

  return (
    <section
      id="cases"
      className="relative z-10 py-24 md:py-36 border-y border-ink/10 overflow-hidden"
      data-testid="casestudies-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="max-w-3xl mb-16 md:mb-20">
          <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
            {t("landing.story_eyebrow")}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink mb-5">
            {t("landing.story_h")}
          </h2>
          <p className="font-body text-base md:text-lg text-ink-soft leading-relaxed">
            {t("landing.story_body")}
          </p>
        </Reveal>

        {/* ---------- Desktop: featured story + compact video card ---------- */}
        <div className="hidden md:grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-14">
          <Reveal className="lg:col-span-7">
            <article data-testid="case-pantelis" className="border-t border-ink/15 pt-8">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-3">
                {t("landing.story_client_role")}
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-ink leading-snug mb-1">{t("landing.story_title")}</h3>
              <p className="font-hand text-lg text-pencil mb-6">{t("landing.story_client")}</p>

              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-2">
                {t("landing.story_situation_label")}
              </p>
              <p className="font-body text-ink-soft leading-relaxed mb-6">{t("landing.story_situation")}</p>

              <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-2">
                {t("landing.story_prep_label")}
              </p>
              <p className="font-body text-ink-soft leading-relaxed mb-6">{t("landing.story_prep")}</p>

              <div className="border-l-2 border-terracotta pl-5 mb-6">
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-2">
                  {t("landing.story_day_label")}
                </p>
                <p className="font-body text-ink leading-relaxed">{t("landing.story_day")}</p>
              </div>

              <TextCTA href={ROUTES.caseStudies} testId="case-pantelis-link">
                {t("landing.story_link")}
              </TextCTA>
            </article>

            {/* Secondary, compact proof — Zahos (project/sales presentations). Visually
                subordinate to Pantelis: smaller heading, no consultation CTA, just a
                quote + link through to his own full case study. */}
            <div className="mt-10 pt-7 border-t border-ink/10" data-testid="case-zahos-compact">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
                {t("landing.zahos_story_eyebrow")}
              </p>
              <h4 className="font-serif text-lg md:text-xl text-ink leading-snug mb-3">
                {t("landing.zahos_story_title")}
              </h4>
              <p className="font-body text-sm text-ink-soft leading-relaxed mb-2">
                {t("landing.zahos_story_description")}
              </p>
              <p className="font-body text-sm text-ink-soft leading-relaxed mb-4">
                {t("landing.zahos_story_outcome")}
              </p>
              <blockquote className="border-l-2 border-terracotta/50 pl-4 mb-4">
                <p className="font-serif italic text-base text-ink leading-snug">
                  &ldquo;{t("landing.zahos_story_quote")}&rdquo;
                </p>
                <p className="mt-2 font-hand text-terracotta text-sm not-italic">
                  — {t("landing.zahos_story_attribution")}
                </p>
              </blockquote>
              <TextCTA href={ZAHOS_CASE_HREF} testId="case-zahos-link">
                {t("landing.zahos_story_link")}
              </TextCTA>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="relative bg-paper border border-ink/12 shadow-[4px_8px_30px_rgba(18,20,24,0.10)] p-5 max-w-sm mx-auto lg:mx-0" data-testid="video-proof-desktop">
              <div
                className="relative w-full bg-ink/[0.06] border border-ink/10 overflow-hidden"
                style={{ paddingBottom: "125%" }}
              >
                {desktopPlaying ? (
                  <VimeoPlayer testId="video-proof-iframe-desktop" />
                ) : (
                  <button
                    type="button"
                    onClick={() => setDesktopPlaying(true)}
                    data-testid="video-proof-play-desktop"
                    className="group absolute inset-0 w-full h-full flex items-center justify-center bg-ink/[0.03]"
                    aria-label={t("landing.video_cta")}
                  >
                    <span className="flex items-center justify-center w-14 h-14 rounded-full bg-ink text-paper group-hover:bg-terracotta transition-colors duration-300">
                      <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
                    </span>
                  </button>
                )}
              </div>
              <div className="pt-4 px-1">
                <p className="font-serif text-lg text-ink leading-snug">{t("landing.video_title")}</p>
                <p className="font-body text-sm text-ink-soft mt-1">
                  {t("landing.video_person")} · {t("landing.video_person_role")}
                </p>
                <p className="font-body text-sm text-ink-soft mt-2 leading-relaxed">{t("landing.video_description")}</p>
                {!desktopPlaying && (
                  <button
                    type="button"
                    onClick={() => setDesktopPlaying(true)}
                    data-testid="video-proof-cta-desktop"
                    className="mt-3 font-body text-sm text-terracotta underline decoration-terracotta/50 underline-offset-4 hover:text-ink transition-colors duration-300"
                  >
                    {t("landing.video_cta")} &rarr;
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        </div>

        {/* ---------- Mobile: compact summary + outcome + click-to-play invite ---------- */}
        <div className="md:hidden mb-10" data-testid="case-pantelis-mobile">
          <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-2">
            {t("landing.story_client_role")}
          </p>
          <p className="font-body text-ink leading-relaxed mb-4" data-testid="case-pantelis-mobile-summary">
            {t("landing.story_mobile_summary")}
          </p>
          <div className="border-l-2 border-terracotta pl-4 mb-4">
            <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-1">
              {t("landing.story_mobile_outcome_label")}
            </p>
            <p className="font-body text-ink-soft leading-relaxed text-sm">{t("landing.story_mobile_outcome")}</p>
          </div>
          <TextCTA href={ROUTES.caseStudies} testId="case-pantelis-link-mobile">
            {t("landing.story_link")}
          </TextCTA>

          {/* Secondary, compact proof — Zahos (mobile) */}
          <div className="mt-8 pt-6 border-t border-ink/10" data-testid="case-zahos-compact-mobile">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              {t("landing.zahos_story_eyebrow")}
            </p>
            <h4 className="font-serif text-lg text-ink leading-snug mb-2">
              {t("landing.zahos_story_title")}
            </h4>
            <p className="font-body text-sm text-ink-soft leading-relaxed mb-2">
              {t("landing.zahos_story_description")}
            </p>
            <p className="font-body text-sm text-ink-soft leading-relaxed mb-3">
              {t("landing.zahos_story_outcome")}
            </p>
            <blockquote className="border-l-2 border-terracotta/50 pl-4 mb-3">
              <p className="font-serif italic text-sm text-ink leading-snug">
                &ldquo;{t("landing.zahos_story_quote")}&rdquo;
              </p>
              <p className="mt-2 font-hand text-terracotta text-sm not-italic">
                — {t("landing.zahos_story_attribution")}
              </p>
            </blockquote>
            <TextCTA href={ZAHOS_CASE_HREF} testId="case-zahos-link-mobile">
              {t("landing.zahos_story_link")}
            </TextCTA>
          </div>

          <div className="mt-8 max-w-xs mx-auto bg-paper-deep/50 border border-ink/10 p-5" data-testid="video-proof-mobile">
            {mobilePlaying ? (
              <div className="relative w-full bg-ink/[0.06] overflow-hidden" style={{ paddingBottom: "177.78%" }}>
                <VimeoPlayer testId="video-proof-iframe-mobile" />
              </div>
            ) : (
              <>
                <p className="font-body text-sm text-ink-soft mb-2" data-testid="video-proof-mobile-prompt">
                  {t("landing.video_mobile_prompt")}
                </p>
                <button
                  type="button"
                  onClick={() => setMobilePlaying(true)}
                  data-testid="video-proof-play-mobile"
                  className="inline-flex items-center gap-2 font-body text-sm text-terracotta underline decoration-terracotta/50 underline-offset-4"
                >
                  <Play className="w-3.5 h-3.5" fill="currentColor" />
                  {t("landing.video_cta")}
                </button>
              </>
            )}
          </div>
        </div>

        <Reveal delay={0.15} className="text-center pt-4">
          <div className="inline-flex flex-col items-center gap-3">
            <PrimaryCTA href={ROUTES.bookPresentation} testId="case-book-cta" location="client_story" targetAssetId="booking_presentation">
              {t("landing.hero_book_cta")}
            </PrimaryCTA>
            <p className="font-body text-sm text-ink-soft" data-testid="case-book-microcopy">
              {t("landing.story_cta_microcopy")}
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
